import React, { useEffect, useState } from 'react';
import BaseOverlay from '../../components/layout/BaseOverlay.jsx';
import BaseAutocomplete from '../../components/form/BaseAutocomplete.jsx';
import BaseTreeSelect from '../../components/form/BaseTreeSelect.jsx';
import BaseDropdown from '../../components/form/BaseDropdown.jsx';
import BaseButton from '../../components/form/BaseButton.jsx';
import { MultiSelect } from 'primereact/multiselect';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import IconClose from '../../components/images/IconClose.jsx';
import IconCancel from '../../components/images/IconCancel.jsx';
import { useDebounce } from '../../hooks/useDebounce.js';
import { useLazyGetCompaniesQuery } from '../../api/contact.js';
import { useLazyGetCalendarContactsQuery } from '../../api/calendar.js';
import { twMerge } from 'tailwind-merge';

const collectLeafIds = (node) => {
    if (!node.children || node.children.length === 0) {
        return [node.id];
    }
    return node.children.reduce((acc, child) => acc.concat(collectLeafIds(child)), []);
};

const AdvancedSearchFilterGlobal = ({
    filters,
    setFilters,
    visible,
    setVisible,
    applyFilters,
    optionsMap,
    treeOptions,
    selectedCountries,
    setSelectedCountries,
    countriesTree
}) => {
    const [companySearch, setCompanySearch] = useState('');
    const [companyList, setCompanyList] = useState([]);
    const [contactSearch, setContactSearch] = useState('');
    const [contactList, setContactList] = useState([]);

    const [getCompanies, { isLoading: isLoadingCompanies }] = useLazyGetCompaniesQuery();
    const [getContacts, { isLoading: isLoadingContacts }] = useLazyGetCalendarContactsQuery();

    useEffect(() => {
        const computedIds = [];
        const traverse = (nodes) => {
            nodes.forEach((node) => {
                if (node.children && node.children.length > 0) {
                    if (selectedCountries[node.key] && selectedCountries[node.key].checked === true) {
                        computedIds.push(...node.children.map((child) => child.id));
                    } else {
                        traverse(node.children);
                    }
                } else {
                    if (selectedCountries[node.key] && selectedCountries[node.key].checked === true) {
                        computedIds.push(node.id);
                    }
                }
            });
        };
        traverse(countriesTree);
        const sortedComputed = [...computedIds].sort();
        const sortedFilters = [...(filters.countries || [])].sort();
        if (sortedComputed.join(',') !== sortedFilters.join(',')) {
            setFilters((prev) => ({ ...prev, countries: computedIds }));
        }
    }, [selectedCountries, countriesTree, setFilters, filters.countries]);

    const debouncedCompanySearch = useDebounce(companySearch, 500);
    const debouncedContactSearch = useDebounce(contactSearch, 500);

    useEffect(() => {
        if (debouncedCompanySearch) {
            (async () => {
                const result = await getCompanies({ search: debouncedCompanySearch });
                setCompanyList(result?.data?.response?.companies || []);
            })();
        } else {
            setCompanyList([]);
        }
    }, [debouncedCompanySearch, getCompanies]);

    useEffect(() => {
        if (debouncedContactSearch) {
            (async () => {
                const result = await getContacts({ search: debouncedContactSearch });
                setContactList(result?.data?.response?.contacts || []);
            })();
        } else {
            setContactList([]);
        }
    }, [debouncedContactSearch, getContacts]);

    const [selectedNeeds, setSelectedNeeds] = useState({});
    const [selectedCapabilities, setSelectedCapabilities] = useState({});

    const changeFilter = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    const renderCountriesTreeSelect = () => (
        <div className='mb-4' key='countries'>
            <h2 className='pb-1 text-md font-bold'>Countries</h2>
            <div className='flex items-center gap-2'>
                <BaseTreeSelect
                    name='Countries'
                    label='Select Countries'
                    placeholder='Select Countries'
                    inputClassName='md:w-20rem w-full rounded-full h-[48px]'
                    value={selectedCountries}
                    setValue={setSelectedCountries}
                    options={countriesTree}
                    selectionMode='checkbox'
                    display='chip'
                    showClear={false}
                    filter={true}
                    filterMode='strict'
                    filterDelay={10}
                    propagateSelection={true}
                    checkStrictly={false}
                />
                {filters.countries && filters.countries.length > 0 && (
                    <div
                        className='cursor-pointer w-9'
                        onClick={() => {
                            setSelectedCountries({});
                            setFilters((prev) => ({ ...prev, countries: [] }));
                        }}
                    >
                        <IconCancel className='fill-theme1-primary' />
                    </div>
                )}
            </div>
        </div>
    );

    const renderTextInput = ({ field, title, placeholder }) => (
        <div className='mb-4' key={field}>
            <h2 className='pb-1 text-md font-bold'>{title}</h2>
            <input
                type='text'
                value={filters[field]}
                onChange={(e) => changeFilter(field, e.target.value)}
                placeholder={placeholder}
                className='w-full bg-transparent border border-theme1-text3 rounded-4xl px-4 py-2'
            />
        </div>
    );

    const renderDropdown = ({ field, title, placeholder, options }) => (
        <div className='mb-4' key={field}>
            <h2 className='pb-1 text-md font-bold'>{title}</h2>
            <BaseDropdown
                id={field}
                name={field}
                value={filters[field]}
                setValue={(val) => changeFilter(field, val)}
                options={options}
                placeholder={placeholder}
            />
        </div>
    );

    const renderLocationsDropdown = ({ field, title, placeholder, options }) => (
        <div className='mb-4' key={field}>
            <h2 className='pb-1 text-md font-bold'>{title}</h2>
            <BaseDropdown
                id={field}
                name={field}
                value={filters[field] || ''}
                setValue={(val) => changeFilter(field, val)}
                options={options}
                placeholder={placeholder}
            />
        </div>
    );

    const renderMultiSelect = ({ field, title, placeholder, optionLabel, optionValue }) => (
        <div className='mb-4' key={field}>
            <h2 className='pb-1 text-md font-bold'>{title}</h2>
            <MultiSelect
                value={Array.isArray(filters[field]) ? filters[field] : []}
                onChange={(e) => changeFilter(field, Array.isArray(e.value) ? e.value : [])}
                options={optionsMap[field] || []}
                optionLabel={optionLabel || 'label'}
                {...(optionValue ? { optionValue } : {})}
                filter
                placeholder={placeholder}
                maxSelectedLabels={3}
                className='bg-transparent border border-theme1-text3 rounded-4xl px-2 w-full'
            />
        </div>
    );

    const renderRangeInputs = ({ field, title, placeholderMin, placeholderMax }) => (
        <div className='mb-4' key={field}>
            <h2 className='pb-1 text-md font-bold'>{title}</h2>
            <div className='flex gap-4 flex-col sm:flex-row'>
                <InputNumber
                    value={filters[field]?.min || ''}
                    onValueChange={(e) => changeFilter(field, { ...filters[field], min: e.value })}
                    placeholder={placeholderMin}
                    className='w-full bg-transparent border border-theme1-text3 rounded-4xl px-4 py-2'
                />
                <InputNumber
                    value={filters[field]?.max || ''}
                    onValueChange={(e) => changeFilter(field, { ...filters[field], max: e.value })}
                    placeholder={placeholderMax}
                    className='w-full bg-transparent border border-theme1-text3 rounded-4xl px-4 py-2'
                />
            </div>
        </div>
    );

    const renderCalendar = ({ field, title, placeholder, view = 'date' }) => (
        <div className='mb-4' key={field}>
            <h2 className='pb-1 text-md font-bold'>{title}</h2>
            <Calendar
                value={filters[field] ? new Date(filters[field]) : null}
                onChange={(e) => changeFilter(field, e.value ? e.value.toISOString().split('T')[0] : '')}
                view={view}
                dateFormat='D, dd.mm.yy'
                placeholder={placeholder}
                className='w-full bg-transparent border border-theme1-text3 rounded-4xl px-4 py-2'
            />
        </div>
    );

    const renderCheckbox = ({ field, title }) => (
        <div className='mb-4' key={field}>
            <label className='inline-flex items-center'>
                <input
                    type='checkbox'
                    checked={filters[field]}
                    onChange={(e) => changeFilter(field, e.target.checked)}
                    className={twMerge(
                        'h-7 w-7 appearance-none cursor-pointer rounded border border-theme1-primary mr-3',
                        'checked:bg-theme1-tan relative',
                        'before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2',
                        'before:content-[""] before:w-4 before:h-4 before:bg-no-repeat before:bg-center before:opacity-0',
                        "checked:before:opacity-100 checked:before:bg-[url('/src/resources/images/checkmark.svg')]"
                    )}
                />
                {title}
            </label>
        </div>
    );

    const renderCompaniesAutocomplete = () => (
        <div className='mb-4'>
            <h2 className='pb-1 text-md font-bold'>Companies</h2>
            <BaseAutocomplete
                inputClassName='text-theme1-text1 rounded-2xl border px-4 py-2 border-theme1-text3 w-full outline-none'
                items={companyList.map((company) => ({
                    label: company.name,
                    value: company.id
                }))}
                onSelect={(id, label) => {
                    changeFilter('companies', [...filters.companies, { id, name: label }]);
                    setCompanySearch('');
                }}
                value={companySearch}
                setValue={setCompanySearch}
                placeholder='Search for a company...'
                isLoading={isLoadingCompanies}
                exclude={filters.companies.map((company) => company.id)}
            />
            <div className='flex flex-wrap gap-2 mt-2'>
                {filters.companies.map((company) => (
                    <span
                        key={company.id}
                        className='flex items-center gap-2 bg-theme1-gold1 pl-3 pr-2 py-1 rounded-4xl text-white'
                    >
                        <span>{company.name}</span>
                        <button
                            type='button'
                            onClick={() =>
                                changeFilter(
                                    'companies',
                                    filters.companies.filter((c) => c.id !== company.id)
                                )
                            }
                        >
                            <IconClose className='fill-white w-4 h-4' />
                        </button>
                    </span>
                ))}
            </div>
        </div>
    );

    const renderContactsAutocomplete = () => (
        <div className='mb-4'>
            <h2 className='pb-1 text-md font-bold'>Contacts</h2>
            <BaseAutocomplete
                inputClassName='text-theme1-text1 rounded-2xl border px-4 py-2 border-theme1-text3 w-full outline-none'
                items={contactList.map((contact) => ({
                    label: `${contact.first_name} ${contact.last_name}`,
                    value: contact.id
                }))}
                onSelect={(id, label) => {
                    const newContact = { contact_id: id, name: label };
                    const updated = Array.isArray(filters.contacts) ? [...filters.contacts, newContact] : [newContact];
                    changeFilter('contacts', updated);
                    setContactSearch('');
                }}
                value={contactSearch}
                setValue={setContactSearch}
                placeholder='Search for a contact...'
                isLoading={isLoadingContacts}
                exclude={Array.isArray(filters.contacts) ? filters.contacts.map((c) => c.contact_id) : []}
            />
            <div className='flex flex-wrap gap-2 mt-2'>
                {Array.isArray(filters.contacts) &&
                    filters.contacts.map((contact) => (
                        <span
                            key={contact.contact_id}
                            className='flex items-center gap-2 bg-theme1-gold1 pl-3 pr-2 py-1 rounded-4xl text-white'
                        >
                            <span>{contact.name}</span>
                            <button
                                type='button'
                                onClick={() =>
                                    changeFilter(
                                        'contacts',
                                        filters.contacts.filter((c) => c.contact_id !== contact.contact_id)
                                    )
                                }
                            >
                                <IconClose className='fill-white w-4 h-4' />
                            </button>
                        </span>
                    ))}
            </div>
        </div>
    );

    const renderTreeSelect = ({ field, title }) => (
        <div className='mb-4' key={field}>
            <h2 className='pb-1 text-md font-bold'>{title}</h2>
            <div className='flex items-center gap-2'>
                <BaseTreeSelect
                    name={title}
                    label={`Select ${title}`}
                    placeholder={`Select ${title}`}
                    inputClassName='md:w-20rem w-full rounded-full h-[48px]'
                    value={field === 'needs' ? selectedNeeds : selectedCapabilities}
                    setValue={field === 'needs' ? setSelectedNeeds : setSelectedCapabilities}
                    options={treeOptions}
                    selectionMode='checkbox'
                    display='chip'
                    showClear={false}
                    filter={true}
                    filterMode='strict'
                    filterDelay={10}
                    onSelect={(data) => {
                        let arr = [];
                        Object.entries(data).forEach(([key, nodeState]) => {
                            const split = key.split('-');
                            let element = treeOptions[split[0]];
                            if (split.length > 1) {
                                split.slice(1).forEach((index) => {
                                    element = element.children[index];
                                });
                            }
                            if (nodeState.checked) {
                                arr.push(element.data);
                            }
                        });
                        changeFilter(field, arr);
                    }}
                />
                {filters[field]?.length > 0 && (
                    <div
                        className='cursor-pointer w-9'
                        onClick={() => {
                            if (field === 'needs') {
                                setSelectedNeeds({});
                            } else {
                                setSelectedCapabilities({});
                            }
                            changeFilter(field, []);
                        }}
                    >
                        <IconCancel className='fill-theme1-primary' />
                    </div>
                )}
            </div>
        </div>
    );

    const simpleSelectChangeFilter = (field, value) => {
        if (filters[field] === value) {
            changeFilter(field, '');
        } else {
            changeFilter(field, value);
        }
    };

    const simpleSelectText = (field, item) => {
        return (
            <span
                key={`${field}-${item.label}`}
                className={`${filters[field] === item.value ? 'text-theme1-white' : 'text-theme1-primary'}`}
            >
                {item.label}
            </span>
        );
    };

    const simpleSelectStars = (field, item) => (
        <div className='inline-flex'>
            {Array.from({ length: item.value }).map((_, index) => {
                return filters[field] === item.value ? (
                    <svg
                        key={`${field}-${item.name}-${index}`}
                        width='24'
                        height='23'
                        viewBox='0 0 44 43'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                        className={index < item.value - 1 ? 'mr-1' : ''}
                    >
                        <path
                            d='M21.6157 3.5835L27.152 14.7993L39.5324 16.6089L30.574 25.3343L32.6882 37.661L21.6157 31.8381L10.5432 37.661L12.6574 25.3343L3.69904 16.6089L16.0795 14.7993L21.6157 3.5835Z'
                            fill='#FFB421'
                            stroke='#FFB421'
                            strokeWidth='3.58333'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                        />
                    </svg>
                ) : (
                    <svg
                        key={`${field}-${item.name}-${index}`}
                        width='24'
                        height='23'
                        viewBox='0 0 44 43'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                        className={index < item.value - 1 ? 'mr-1' : ''}
                    >
                        <path
                            d='M21.6157 3.5835L27.152 14.7993L39.5324 16.6089L30.574 25.3343L32.6882 37.661L21.6157 31.8381L10.5432 37.661L12.6574 25.3343L3.69904 16.6089L16.0795 14.7993L21.6157 3.5835Z'
                            fill='#D4C7C7'
                            stroke='#D4C7C7'
                            strokeWidth='3.58333'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                        />
                    </svg>
                );
            })}
        </div>
    );

    const renderSimpleSelect = ({ title, field, elements, options }) => {
        return (
            <div className='mb-[30px]' key={field}>
                <h2 className='pb-[10px] text-md font-bold'>{title}</h2>
                <div>
                    {options.map((item, index) => (
                        <div
                            key={`${field}-${item.value}-${index}`}
                            className={`inline-flex mr-3 w-min rounded-4xl border border-theme1-text3 px-4 py-2 mb-2 items-center ${
                                filters[field] === item.value ? 'bg-theme1-primary' : ''
                            }`}
                            onClick={() => simpleSelectChangeFilter(field, item.value)}
                        >
                            {elements(field, item)}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderYearPicker = ({ field, title }) => {
        return (
            <div className='mb-4' key={field}>
                <h2 className='pb-[10px] text-md font-bold'>{title}</h2>
                <Calendar
                    placeholder={title}
                    value={filters[field] ? new Date(filters[field], 0) : null}
                    onChange={(e) => {
                        const year = e.value ? e.value.getFullYear().toString() : '';
                        changeFilter(field, year);
                    }}
                    view='year'
                    dateFormat='yy'
                    className='w-full bg-transparent border-theme1-text3 border rounded-4xl px-4 py-2'
                />
            </div>
        );
    };

    return (
        <BaseOverlay show={visible} className='overflow-auto h-[100dvh] rounded-none'>
            <div className='min-h-96 max-w-3xl w-full mx-auto p-4'>
                <div className='flex justify-end py-4'>
                    <div className='text-theme1-primary font-bold cursor-pointer' onClick={() => setVisible(false)}>
                        Close
                    </div>
                </div>
                <div className='text-center text-theme1-title2 font-bold text-2xl mb-4'>Filter</div>

                <div className='mb-6'>
                    <h2 className='pb-1 mb-4 text-xl font-bold border-b border-solid border-theme1-tanTransparent'>
                        General
                    </h2>
                    {renderCompaniesAutocomplete()}
                    {renderContactsAutocomplete()}
                    {renderTreeSelect({ field: 'needs', title: 'Needs' })}
                    {renderTreeSelect({ field: 'capabilities', title: 'Capabilities' })}
                    {renderCountriesTreeSelect()}
                </div>

                <div className='mb-6'>
                    <h2 className='pb-1 mb-4 text-xl font-bold border-b border-solid border-theme1-tanTransparent'>
                        Projects
                    </h2>
                    {renderDropdown({
                        field: 'status',
                        title: 'Status',
                        placeholder: 'Select status',
                        options: optionsMap.status
                    })}
                    {renderRangeInputs({
                        field: 'budget',
                        title: 'Budget',
                        placeholderMin: 'Min',
                        placeholderMax: 'Max'
                    })}
                    {renderRangeInputs({
                        field: 'actual_costs',
                        title: 'Actual Costs',
                        placeholderMin: 'Min',
                        placeholderMax: 'Max'
                    })}
                </div>

                <div className='mb-6'>
                    <h2 className='pb-1 mb-4 text-xl font-bold border-b border-solid border-theme1-tanTransparent'>
                        Organizations
                    </h2>
                    {renderYearPicker({ field: 'founded', title: 'Founded' })}
                    {renderTextInput({
                        field: 'industry',
                        title: 'Industry',
                        placeholder: 'Enter industry'
                    })}
                    {renderDropdown({
                        field: 'ownership',
                        title: 'Ownership',
                        placeholder: 'Select ownership type',
                        options: optionsMap.ownership
                    })}
                    {renderRangeInputs({
                        field: 'employees',
                        title: 'Employees',
                        placeholderMin: 'Min',
                        placeholderMax: 'Max'
                    })}
                    {renderRangeInputs({
                        field: 'revenue',
                        title: 'Revenue',
                        placeholderMin: 'Min',
                        placeholderMax: 'Max'
                    })}
                    {renderRangeInputs({
                        field: 'profit',
                        title: 'Profit',
                        placeholderMin: 'Min',
                        placeholderMax: 'Max'
                    })}
                    {renderRangeInputs({
                        field: 'expenses',
                        title: 'Expenses',
                        placeholderMin: 'Min',
                        placeholderMax: 'Max'
                    })}
                </div>

                <div className='mb-6'>
                    <h2 className='pb-1 mb-4 text-xl font-bold border-b border-solid border-theme1-tanTransparent'>
                        Meetings
                    </h2>
                    {renderSimpleSelect({
                        field: 'date',
                        title: 'Date',
                        elements: simpleSelectText,
                        options: [
                            { label: 'Past', value: 'past' },
                            { label: 'Future', value: 'future' }
                        ]
                    })}
                    {renderCalendar({
                        field: 'start_date',
                        title: 'Start Date',
                        placeholder: 'DD-MM-YYYY'
                    })}
                    {renderCalendar({
                        field: 'end_date',
                        title: 'End Date',
                        placeholder: 'DD-MM-YYYY'
                    })}
                    {renderLocationsDropdown({
                        field: 'locations',
                        title: 'Locations',
                        placeholder: 'Select location',
                        options: optionsMap.locations
                    })}
                </div>

                <div className='mb-6'>
                    <h2 className='pb-1 mb-4 text-xl font-bold border-b border-solid border-theme1-tanTransparent'>
                        Files
                    </h2>
                    {renderDropdown({
                        field: 'type',
                        title: 'File Type',
                        placeholder: 'Select file type',
                        options: optionsMap.type
                    })}
                </div>

                <div className='mb-6'>
                    <h2 className='pb-1 mb-4 text-xl font-bold border-b border-solid border-theme1-tanTransparent'>
                        Contacts
                    </h2>
                    {renderSimpleSelect({
                        field: 'ranking',
                        title: 'Rep Ranking',
                        elements: simpleSelectStars,
                        options: [
                            { label: '1 Star', value: 1 },
                            { label: '2 Stars', value: 2 },
                            { label: '3 Stars', value: 3 },
                            { label: '4 Stars', value: 4 },
                            { label: '5 Stars', value: 5 }
                        ]
                    })}
                </div>
                <div className='flex justify-between gap-4 w-full mt-6'>
                    <BaseButton
                        className='bg-theme1-primary text-theme1-white'
                        textValue='Apply Filters'
                        onClick={() => {
                            applyFilters();
                            setVisible(false);
                        }}
                    />
                </div>
            </div>
        </BaseOverlay>
    );
};

export default AdvancedSearchFilterGlobal;
